import { SiteUrls } from '@tableau/shared-urls';
import React from 'react';
import { Redirect, Route, RouteComponentProps, Switch } from 'react-router-dom';

import { Messages } from '../../../../../gen-src/Localize';
import {
  FeatureFlags,
  minWidthClass,
  SiteUrlsContext,
  VerticalFlexContainer,
  vizPortalFontClass
} from '../../../data-catalog-shared';
import { BrowserTitleUpdater, BrowserTitleUpdateType } from '../BrowserTitleUpdater';
import { DatabasePlacePage } from '../DatabasePlacePage/DatabasePlacePage';
import { TablePlacePage } from '../TablePlacePage/TablePlacePage';
import { DatabasesBreadcrumbsUpdater } from '../breadcrumbs/DatabasesBreadcrumbsUpdater';
import { TablesBreadcrumbsUpdater } from '../breadcrumbs/TablesBreadcrumbsUpdater';
import { RegisterErrorStoreInHostProps } from '../error/components/RegisterErrorStoreInHost';
import { UnrecognizedUrl } from '../error/components/UnrecognizedUrl';
import { CatalogBrowserSubplace } from './CatalogBrowserHeader';
import { CatalogBrowserPlacePageLayout } from './CatalogBrowserPlacePageLayout';
import { WebClientApiContentTypeCountsResponse } from './CatalogBrowserTypes';

export type CatalogBrowserProps =
  RegisterErrorStoreInHostProps &
  {
    readonly renderDatabasesExplorer: (routeLocationSearch: string) => React.ReactNode;
    readonly renderTablesExplorer: (routeLocationSearch: string) => React.ReactNode;
    readonly hideLaunchEditOnMobile?: boolean;
    readonly newFlowFromCatalog?: boolean;
    readonly projectIdForNewDocuments?: string;
    readonly featureFlags: FeatureFlags;
    readonly webClientResult?: WebClientApiContentTypeCountsResponse;
  };

export class CatalogBrowser extends React.Component<CatalogBrowserProps> {
  public override render(): JSX.Element {
    return (
      <VerticalFlexContainer fillFlexParent={true} additionalClassNames={[vizPortalFontClass, minWidthClass]}>
        <SiteUrlsContext.Consumer>
          {urls =>
            <Route path={urls.catalogPath()} render={() => this.renderCatalogRoute(urls)} />
          }
        </SiteUrlsContext.Consumer>
      </VerticalFlexContainer>
    );
  }

  private renderCatalogRoute = (urls: SiteUrls): JSX.Element => {
    return (
      <Switch>
        <Route
          exact={true}
          path={urls.catalogDatabasesPath()}
          render={this.renderDatabasesPlacePage}
        />
        <Route
          exact={true}
          path={urls.catalogTablesPath()}
          render={this.renderTablesPlacePage}
        />

        <Route
          path={urls.catalogDatabasePath(':databaseId')}
          render={({ match }: RouteComponentProps<{ databaseId: string }>) =>
            <DatabasePlacePage
              hideLaunchEditOnMobile={this.props.hideLaunchEditOnMobile}
              newFlowFromCatalog={this.props.newFlowFromCatalog}
              projectIdForNewDocuments={this.props.projectIdForNewDocuments}
              databaseId={match.params.databaseId}
            />
          }
        />
        <Route
          path={urls.catalogTablePath(':tableId')}
          render={({ match }: RouteComponentProps<{ tableId: string }>) =>
            <TablePlacePage
              hideLaunchEditOnMobile={this.props.hideLaunchEditOnMobile}
              newFlowFromCatalog={this.props.newFlowFromCatalog}
              projectIdForNewDocuments={this.props.projectIdForNewDocuments}
              tableId={match.params.tableId}
            />
          }
        />
        <Redirect exact={true} from={urls.catalogPath()} to={urls.catalogDatabasesPath()} />
        <UnrecognizedUrl
          backToButtonLabel={Messages.BackToCatalog()}
          backToButtonUrl={urls.catalogPath()}
        />
      </Switch>
    );
  }

  private renderDatabasesPlacePage = (routeProps: RouteComponentProps): JSX.Element => {
    return (
      <>
        <BrowserTitleUpdater
          nameFunction={Messages.DatabasesAndFiles}
          browserTitleUpdateType={BrowserTitleUpdateType.TITLE_WITH_PLACE_NAME_FUNCTION}
        />
        <DatabasesBreadcrumbsUpdater />
        <CatalogBrowserPlacePageLayout
          selectedSubplace={CatalogBrowserSubplace.Databases}
          locationSearch={routeProps.location.search}
          renderExplorerComponent={this.props.renderDatabasesExplorer}
          registerErrorStore={this.props.registerErrorStore}
          unregisterErrorStore={this.props.unregisterErrorStore}
          webClientResult={this.props.webClientResult}
        />
      </>
    );
  }

  private renderTablesPlacePage = (routeProps: RouteComponentProps): JSX.Element => {
    return (
      <>
        <BrowserTitleUpdater
          nameFunction={Messages.Tables}
          browserTitleUpdateType={BrowserTitleUpdateType.TITLE_WITH_PLACE_NAME_FUNCTION}
        />
        <TablesBreadcrumbsUpdater />
        <CatalogBrowserPlacePageLayout
          selectedSubplace={CatalogBrowserSubplace.Tables}
          locationSearch={routeProps.location.search}
          renderExplorerComponent={this.props.renderTablesExplorer}
          registerErrorStore={this.props.registerErrorStore}
          unregisterErrorStore={this.props.unregisterErrorStore}
          webClientResult={this.props.webClientResult}
        />
      </>
    );
  }
}
